import { useEffect, useMemo, useState } from 'react'
import { Trash2 } from 'lucide-react'
import { useProfile, candidateSections, type ProfilePaper } from '@/hooks/use-profile'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { authorizedFetch } from '@/lib/api'
import {
  ArtifactCanvasBody,
  ArtifactCanvasFooterBar,
  ArtifactCanvasInlineError,
  ArtifactCanvasSaveActions,
} from '@/components/artifact/artifact-canvas-layout'

interface PaperCanvasProps {
  paper: ProfilePaper | null
  onClose: () => void
}

interface PaperDraft {
  title: string
  authors: string
  venue: string
  year: string
  url: string
  doi: string
  description: string
}

function buildDraft(paper: ProfilePaper | null): PaperDraft {
  return {
    title: paper?.title ?? '',
    authors: paper?.authors ?? '',
    venue: paper?.venue ?? '',
    year: paper?.year != null ? String(paper.year) : '',
    url: paper?.url ?? '',
    doi: paper?.doi ?? '',
    description: paper?.description ?? '',
  }
}

function parseYear(value: string): number | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  const year = Number(trimmed)
  if (!Number.isInteger(year) || year < 1000 || year > 2100) return null
  return year
}

export function PaperCanvas({ paper, onClose }: PaperCanvasProps) {
  const { profile, addPaperAsync, updatePaperAsync, deletePaperAsync } = useProfile()
  const candidate = candidateSections(profile)

  const isEditMode = paper !== null && !!paper.id

  const livePaper = useMemo<ProfilePaper | null>(() => {
    if (!isEditMode || !paper?.id) return null
    const match = (candidate.papers ?? []).find((row) => row.id === paper.id)
    return match ?? paper
  }, [isEditMode, paper, candidate.papers])

  const initialDraft = useMemo(
    () => buildDraft(livePaper ?? paper),
    [livePaper, paper],
  )
  const [draft, setDraft] = useState<PaperDraft>(initialDraft)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [lookupNotice, setLookupNotice] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isLookingUp, setIsLookingUp] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  useEffect(() => {
    setDraft(buildDraft(livePaper ?? paper))
    setValidationError(null)
    setSubmitError(null)
    setLookupNotice(null)
    setConfirmingDelete(false)
  }, [livePaper, paper])

  const fieldsDirty =
    draft.title !== initialDraft.title ||
    draft.authors !== initialDraft.authors ||
    draft.venue !== initialDraft.venue ||
    draft.year !== initialDraft.year ||
    draft.url !== initialDraft.url ||
    draft.doi !== initialDraft.doi ||
    draft.description !== initialDraft.description
  const isDirty = isEditMode ? fieldsDirty : true

  const handleLookup = async () => {
    setValidationError(null)
    setSubmitError(null)
    setLookupNotice(null)
    if (!draft.doi.trim()) {
      setValidationError('Paste a DOI first, then look it up.')
      return
    }
    setIsLookingUp(true)
    try {
      const response = await authorizedFetch('/api/profiles/my/papers/lookup', {
        method: 'POST',
        body: JSON.stringify({ doi: draft.doi.trim() }),
      })
      const data = (await response.json().catch(() => ({}))) as {
        found?: boolean
        title?: string | null
        authors?: string | null
        venue?: string | null
        year?: number | null
        url?: string | null
        doi?: string | null
        description?: string | null
        error?: string | null
      }
      if (!response.ok || !data.found) {
        setLookupNotice(
          data.error || 'Could not find that DOI. You can still save what you typed.',
        )
        return
      }
      setDraft((current) => ({
        ...current,
        title: data.title?.trim() || current.title,
        authors: data.authors?.trim() || current.authors,
        venue: data.venue?.trim() || current.venue,
        year: data.year != null ? String(data.year) : current.year,
        url: data.url?.trim() || current.url,
        doi: data.doi?.trim() || current.doi,
        description: data.description?.trim() || current.description,
      }))
      setLookupNotice('Filled from Crossref. Check the title, then save.')
    } catch (err) {
      setLookupNotice(
        err instanceof Error
          ? err.message
          : 'DOI lookup failed. You can still save what you typed.',
      )
    } finally {
      setIsLookingUp(false)
    }
  }

  const handleSave = async () => {
    setValidationError(null)
    setSubmitError(null)

    if (!draft.title.trim()) {
      setValidationError('Title is required.')
      return
    }
    if (draft.year.trim() && parseYear(draft.year) == null) {
      setValidationError('Year must be a four-digit year, or leave it empty.')
      return
    }

    const year = parseYear(draft.year)

    setIsSaving(true)
    try {
      if (!isEditMode) {
        await addPaperAsync({
          title: draft.title.trim(),
          authors: draft.authors.trim() || undefined,
          venue: draft.venue.trim() || undefined,
          year,
          url: draft.url.trim() || undefined,
          doi: draft.doi.trim() || undefined,
          description: draft.description.trim() || undefined,
        })
      } else if (livePaper?.id && fieldsDirty) {
        await updatePaperAsync({
          id: livePaper.id,
          updates: {
            title: draft.title.trim(),
            authors: draft.authors.trim(),
            venue: draft.venue.trim(),
            year,
            url: draft.url.trim(),
            doi: draft.doi.trim(),
            description: draft.description.trim(),
          },
        })
      }

      onClose()
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to save publication.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!isEditMode || !livePaper?.id) return

    if (!confirmingDelete) {
      setConfirmingDelete(true)
      return
    }

    setSubmitError(null)
    setIsDeleting(true)
    try {
      await deletePaperAsync(livePaper.id)
      onClose()
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to delete publication.')
      setIsDeleting(false)
      setConfirmingDelete(false)
    }
  }

  const isBusy = isSaving || isDeleting || isLookingUp

  return (
    <ArtifactCanvasBody className="flex h-full min-h-0 flex-col space-y-0 p-0">
      <form
        className="min-h-0 flex-1 space-y-6 overflow-y-auto p-6"
        onSubmit={(event) => event.preventDefault()}
      >
        <div className="space-y-2">
          <Label htmlFor="artifact-paper-title">Title *</Label>
          <Input
            id="artifact-paper-title"
            value={draft.title}
            onChange={(event) => {
              setDraft((current) => ({ ...current, title: event.target.value }))
              setValidationError(null)
            }}
            placeholder="Paper title"
            disabled={isBusy}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="artifact-paper-authors">Authors</Label>
          <Input
            id="artifact-paper-authors"
            value={draft.authors}
            onChange={(event) => {
              setDraft((current) => ({ ...current, authors: event.target.value }))
            }}
            placeholder="Jane Doe, John Smith"
            disabled={isBusy}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="artifact-paper-venue">Venue</Label>
            <Input
              id="artifact-paper-venue"
              value={draft.venue}
              onChange={(event) => {
                setDraft((current) => ({ ...current, venue: event.target.value }))
              }}
              placeholder="Journal or conference"
              disabled={isBusy}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="artifact-paper-year">Year</Label>
            <Input
              id="artifact-paper-year"
              inputMode="numeric"
              value={draft.year}
              onChange={(event) => {
                setDraft((current) => ({ ...current, year: event.target.value }))
                setValidationError(null)
              }}
              placeholder="2024"
              disabled={isBusy}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="artifact-paper-url">URL</Label>
          <Input
            id="artifact-paper-url"
            value={draft.url}
            onChange={(event) => {
              setDraft((current) => ({ ...current, url: event.target.value }))
            }}
            placeholder="https://…"
            disabled={isBusy}
          />
          <p className="text-xs text-muted-foreground">Strongly recommended so people can open the paper.</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="artifact-paper-doi">DOI</Label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              id="artifact-paper-doi"
              value={draft.doi}
              onChange={(event) => {
                setDraft((current) => ({ ...current, doi: event.target.value }))
                setValidationError(null)
                setLookupNotice(null)
              }}
              placeholder="10.1234/example"
              disabled={isBusy}
            />
            <Button
              type="button"
              variant="outline"
              onClick={handleLookup}
              disabled={isBusy}
              className="shrink-0"
            >
              {isLookingUp ? 'Looking up…' : 'Look up DOI'}
            </Button>
          </div>
          {lookupNotice ? (
            <p className="text-xs text-muted-foreground" role="status">
              {lookupNotice}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              Paste a DOI to fill the fields. If lookup fails, you can still save what you typed.
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="artifact-paper-description">Description</Label>
          <Textarea
            id="artifact-paper-description"
            rows={4}
            value={draft.description}
            onChange={(event) => {
              setDraft((current) => ({ ...current, description: event.target.value }))
            }}
            placeholder="Optional abstract or note"
            disabled={isBusy}
          />
        </div>
      </form>

      <ArtifactCanvasInlineError>{validationError || submitError}</ArtifactCanvasInlineError>

      <ArtifactCanvasFooterBar
        leading={
          isEditMode ? (
            <Button
              type="button"
              variant={confirmingDelete ? 'destructive' : 'ghost'}
              onClick={handleDelete}
              disabled={isBusy}
              className={confirmingDelete ? '' : 'text-destructive hover:text-destructive'}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              {isDeleting ? 'Deleting...' : confirmingDelete ? 'Confirm delete' : 'Delete'}
            </Button>
          ) : undefined
        }
        actions={
          <ArtifactCanvasSaveActions
            onClose={onClose}
            onSave={handleSave}
            isDirty={isDirty}
            isSaving={isSaving}
            saveLabel={isEditMode ? 'Save' : 'Add publication'}
            savingLabel="Saving..."
            closeDisabled={isBusy}
            saveDisabled={!isDirty || isBusy}
          />
        }
      />
    </ArtifactCanvasBody>
  )
}
